import React, { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Bot,
  Plus,
  Pencil,
  Trash2,
  MessageSquare,
  Activity,
  ChevronRight,
  Search,
  Zap,
  CheckCircle,
  XCircle,
  Clock,
  User,
  ArrowLeft,
  AlertTriangle,
  FlaskConical,
} from "lucide-react";
import { Link } from "wouter";
import { AiAgentOperationsOverview } from "@/components/super-admin/AiAgentOperationsOverview";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface FaqItem {
  question: string;
  answer: string;
}

// ─── Formulário de Agente ─────────────────────────────────────────────────────

interface AgentFormData {
  salonId: number;
  name: string;
  channel: "whatsapp" | "chat_web" | "ambos";
  personality: string;
  welcomeMessage: string;
  tone: "friendly" | "formal" | "casual";
  canBook: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  canAnswerFaq: boolean;
  maxResponseLength: number;
  voiceReplyEnabled: boolean;
  voiceReplyVoiceId: string;
  voiceReplyMaxChars: number;
  faqItems: FaqItem[];
}

const defaultForm: AgentFormData = {
  salonId: 0,
  name: "Assistente",
  channel: "ambos",
  personality: "",
  welcomeMessage: "Olá! Sou a assistente virtual do salão. Como posso ajudar você hoje?",
  tone: "friendly",
  canBook: true,
  canCancel: true,
  canReschedule: true,
  canAnswerFaq: true,
  maxResponseLength: 500,
  voiceReplyEnabled: false,
  voiceReplyVoiceId: "",
  voiceReplyMaxChars: 320,
  faqItems: [],
};

// ─── AgentForm (componente externo para evitar remontagem ao digitar) ───────────

interface AgentFormProps {
  form: AgentFormData;
  setForm: React.Dispatch<React.SetStateAction<AgentFormData>>;
  newFaq: { question: string; answer: string };
  setNewFaq: React.Dispatch<React.SetStateAction<{ question: string; answer: string }>>;
  showSalonSelect: boolean;
  salons: any[];
  voiceProvider: { configured: boolean; voices: any[] } | undefined;
}

function AgentForm({ form, setForm, newFaq, setNewFaq, showSalonSelect, salons, voiceProvider }: AgentFormProps) {
  const addFaq = () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) return;
    setForm((f) => ({ ...f, faqItems: [...f.faqItems, { ...newFaq }] }));
    setNewFaq({ question: "", answer: "" });
  };

  const removeFaq = (idx: number) => {
    setForm((f) => ({ ...f, faqItems: f.faqItems.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      {/* Salão (só no criar) */}
      {showSalonSelect && (
        <div className="space-y-1">
          <Label>Salão</Label>
          <Select
            value={form.salonId ? String(form.salonId) : ""}
            onValueChange={(v) => setForm((f) => ({ ...f, salonId: parseInt(v) }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o salão..." />
            </SelectTrigger>
            <SelectContent>
              {salons.map((s: any) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {salons.length === 0 && (
            <p className="text-xs text-muted-foreground">Nenhum salão cadastrado ainda.</p>
          )}
        </div>
      )}

      {/* Nome */}
      <div className="space-y-1">
        <Label>Nome do Agente</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Ex: Sofia, Luna, Assistente..."
        />
      </div>

      {/* Canal */}
      <div className="space-y-1">
        <Label>Canal de Atuação</Label>
        <Select value={form.channel} onValueChange={(v: any) => setForm((f) => ({ ...f, channel: v }))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ambos">Ambos (WhatsApp + Chat Web)</SelectItem>
            <SelectItem value="whatsapp">Apenas WhatsApp</SelectItem>
            <SelectItem value="chat_web">Apenas Chat Web</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Define em qual canal este agente responderá mensagens.</p>
      </div>

      {/* Tom */}
      <div className="space-y-1">
        <Label>Tom de Voz</Label>
        <Select value={form.tone} onValueChange={(v: any) => setForm((f) => ({ ...f, tone: v }))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="friendly">Amigável e descontraído</SelectItem>
            <SelectItem value="formal">Formal e profissional</SelectItem>
            <SelectItem value="casual">Casual e divertido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mensagem de boas-vindas */}
      <div className="space-y-1">
        <Label>Mensagem de Boas-vindas</Label>
        <Textarea
          value={form.welcomeMessage}
          onChange={(e) => setForm((f) => ({ ...f, welcomeMessage: e.target.value }))}
          placeholder="Olá! Como posso ajudar?"
          rows={2}
        />
      </div>

      {/* Personalidade */}
      <div className="space-y-1">
        <Label>Instruções de Personalidade</Label>
        <Textarea
          value={form.personality}
          onChange={(e) => setForm((f) => ({ ...f, personality: e.target.value }))}
          placeholder="Descreva o comportamento, restrições ou estilo do agente. Ex: Sempre mencione promoções do mês. Nunca ofereça desconto sem autorização..."
          rows={3}
        />
      </div>

      {/* Capacidades */}
      <div className="space-y-2">
        <Label>Capacidades</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: "canBook", label: "Criar agendamentos" },
            { key: "canCancel", label: "Cancelar agendamentos" },
            { key: "canReschedule", label: "Reagendar" },
            { key: "canAnswerFaq", label: "Responder perguntas" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2 p-2 rounded border">
              <Switch
                checked={form[key as keyof AgentFormData] as boolean}
                onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
              />
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Limite de caracteres */}
      <div className="space-y-1">
        <Label>Limite de Resposta (caracteres)</Label>
        <Input
          type="number"
          min={100}
          max={2000}
          value={form.maxResponseLength}
          onChange={(e) => setForm((f) => ({ ...f, maxResponseLength: parseInt(e.target.value) || 500 }))}
        />
      </div>

      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <Label htmlFor="voice-reply">Resposta em áudio no WhatsApp</Label>
            <p className="text-xs text-muted-foreground">
              Envia voz somente quando a cliente enviar um áudio. Valores, links, pagamentos e dados sensíveis continuam em texto.
            </p>
          </div>
          <Switch
            id="voice-reply"
            checked={form.voiceReplyEnabled}
            onCheckedChange={(value) => setForm((current) => ({ ...current, voiceReplyEnabled: value }))}
          />
        </div>

        {form.voiceReplyEnabled && (
          <>
            {!voiceProvider?.configured ? (
              <p className="rounded-lg bg-background p-2 text-xs text-muted-foreground">
                Conecte a chave do ElevenLabs para listar e usar as vozes disponíveis. Até isso acontecer, a agente responderá em texto normalmente.
              </p>
            ) : (
              <div className="space-y-1">
                <Label>Voz escolhida</Label>
                <Select
                  value={form.voiceReplyVoiceId || undefined}
                  onValueChange={(value) => setForm((current) => ({ ...current, voiceReplyVoiceId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha a voz da agente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(voiceProvider.voices ?? []).map((voice: any) => (
                      <SelectItem key={voice.id} value={voice.id}>
                        {voice.name}{voice.labels?.gender ? ` · ${voice.labels.gender}` : ""}{voice.labels?.accent ? ` · ${voice.labels.accent}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(voiceProvider.voices ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhuma voz disponível na conta conectada.</p>
                )}
              </div>
            )}

            <div className="space-y-1">
              <Label>Máximo da resposta em áudio</Label>
              <Input
                type="number"
                min={80}
                max={360}
                value={form.voiceReplyMaxChars}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  voiceReplyMaxChars: Math.min(360, Math.max(80, Number(event.target.value) || 320)),
                }))}
              />
            </div>
          </>
        )}
      </div>

      {/* FAQ */}
      <div className="space-y-2">
        <Label>Perguntas Frequentes (FAQ)</Label>
        {form.faqItems.map((faq, idx) => (
          <div key={idx} className="p-2 rounded border bg-muted/30 text-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium">{faq.question}</p>
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeFaq(idx)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
        <div className="space-y-1">
          <Input
            value={newFaq.question}
            onChange={(e) => setNewFaq((f) => ({ ...f, question: e.target.value }))}
            placeholder="Pergunta..."
          />
          <Input
            value={newFaq.answer}
            onChange={(e) => setNewFaq((f) => ({ ...f, answer: e.target.value }))}
            placeholder="Resposta..."
          />
          <Button variant="outline" size="sm" onClick={addFaq} className="w-full">
            <Plus className="h-3 w-3 mr-1" /> Adicionar FAQ
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function SuperAdminAiAgents() {
  const utils = trpc.useUtils();

  const [search, setSearch] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [form, setForm] = useState<AgentFormData>(defaultForm);
  const [editAgentId, setEditAgentId] = useState<number | null>(null);
  const [deleteAgentId, setDeleteAgentId] = useState<number | null>(null);
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });
  const [activeAgentTab, setActiveAgentTab] = useState("config");
  const [healthFilter, setHealthFilter] = useState<"todos" | "saudavel" | "atencao" | "critico">("todos");

  // Queries
  const { data: agents = [], isLoading: loadingAgents } = (trpc as any).superAdmin.listAiAgents.useQuery();
  const { data: operationalOverview, isLoading: loadingOperationalOverview } = (trpc as any).superAdmin.aiAgentOperationalOverview.useQuery();
  const elevenLabsVoiceQuery = (trpc as any).superAdmin.listElevenLabsVoices?.useQuery?.();
  const voiceProvider = elevenLabsVoiceQuery?.data;
  const { data: allSalonsData } = trpc.superAdmin.listSalons.useQuery({ page: 1, pageSize: 500 });
  const allSalons = allSalonsData?.data ?? [];
  const { data: sessions = [] } = (trpc as any).superAdmin.getAiAgentSessions.useQuery(
    { agentId: selectedAgentId!, limit: 50 },
    { enabled: !!selectedAgentId }
  );
  const { data: messages = [] } = (trpc as any).superAdmin.getAiSessionMessages.useQuery(
    { sessionId: selectedSessionId! },
    { enabled: !!selectedSessionId }
  );

  // Mutations
  const createMutation = (trpc as any).superAdmin.createAiAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente criado com sucesso!");
      (utils as any).superAdmin.listAiAgents.invalidate();
      setShowCreateDialog(false);
      setForm(defaultForm);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateMutation = (trpc as any).superAdmin.updateAiAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente atualizado!");
      (utils as any).superAdmin.listAiAgents.invalidate();
      setShowEditDialog(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMutation = (trpc as any).superAdmin.deleteAiAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente removido.");
      (utils as any).superAdmin.listAiAgents.invalidate();
      setShowDeleteDialog(false);
      if (selectedAgentId === deleteAgentId) setSelectedAgentId(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Todos os salões disponíveis para o select de criação
  // Um salão pode ter múltiplos agentes (ex: um para WhatsApp, outro para chat web)
  const salonsWithoutAgent = allSalons;

  // Filtro de busca
  const filteredAgents = agents.filter((a: any) => {
    const salon = allSalons.find((s: any) => s.id === a.salonId);
    const operationalAgent = operationalOverview?.agents?.find((item: any) => item.id === a.id);
    return (
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      (salon?.name?.toLowerCase().includes(search.toLowerCase()) ?? false)
    ) && (healthFilter === "todos" || operationalAgent?.health === healthFilter);
  });

  // Helpers de formulário
  const openCreate = () => {
    setForm(defaultForm);
    setShowCreateDialog(true);
  };

  const openEdit = (agent: any) => {
    let faqItems: FaqItem[] = [];
    try {
      faqItems = agent.faqItems ? JSON.parse(agent.faqItems) : [];
    } catch {}
    setForm({
      salonId: agent.salonId,
      name: agent.name,
      channel: ((agent as any).channel as any) ?? "ambos",
      personality: agent.personality ?? "",
      welcomeMessage: agent.welcomeMessage ?? "",
      tone: (agent.tone as any) ?? "friendly",
      canBook: agent.canBook,
      canCancel: agent.canCancel,
      canReschedule: agent.canReschedule,
      canAnswerFaq: agent.canAnswerFaq,
      maxResponseLength: agent.maxResponseLength ?? 500,
      voiceReplyEnabled: agent.voiceReplyEnabled ?? false,
      voiceReplyVoiceId: agent.voiceReplyVoiceId ?? "",
      voiceReplyMaxChars: agent.voiceReplyMaxChars ?? 320,
      faqItems,
    });
    setEditAgentId(agent.id);
    setShowEditDialog(true);
  };

  const handleCreate = () => {
    createMutation.mutate({
      salonId: form.salonId,
      name: form.name,
      channel: form.channel,
      personality: form.personality || undefined,
      welcomeMessage: form.welcomeMessage || undefined,
      tone: form.tone,
      canBook: form.canBook,
      canCancel: form.canCancel,
      canReschedule: form.canReschedule,
      canAnswerFaq: form.canAnswerFaq,
      maxResponseLength: form.maxResponseLength,
      voiceReplyEnabled: form.voiceReplyEnabled,
      voiceReplyOnlyForAudio: true,
      voiceReplyVoiceId: form.voiceReplyVoiceId || null,
      voiceReplyMaxChars: form.voiceReplyMaxChars,
      faqItems: form.faqItems.length > 0 ? JSON.stringify(form.faqItems) : undefined,
    });
  };

  const handleUpdate = () => {
    if (!editAgentId) return;
    updateMutation.mutate({
      id: editAgentId,
      name: form.name,
      channel: form.channel,
      personality: form.personality || undefined,
      welcomeMessage: form.welcomeMessage || undefined,
      tone: form.tone,
      canBook: form.canBook,
      canCancel: form.canCancel,
      canReschedule: form.canReschedule,
      canAnswerFaq: form.canAnswerFaq,
      maxResponseLength: form.maxResponseLength,
      voiceReplyEnabled: form.voiceReplyEnabled,
      voiceReplyOnlyForAudio: true,
      voiceReplyVoiceId: form.voiceReplyVoiceId || null,
      voiceReplyMaxChars: form.voiceReplyMaxChars,
      faqItems: form.faqItems.length > 0 ? JSON.stringify(form.faqItems) : undefined,
    });
  };

  const getSalonName = (salonId: number) =>
    allSalons.find((s: any) => s.id === salonId)?.name ?? `Salão #${salonId}`;

  const selectedAgent = agents.find((a: any) => a.id === selectedAgentId);
  const selectedOperationalAgent = operationalOverview?.agents?.find((agent: any) => agent.id === selectedAgentId);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Link href="/superadmin">
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Painel Super Admin
          </Button>
        </Link>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            Agentes de IA
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure assistentes virtuais nativos para cada salão. Visível apenas para o Super Admin.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Agente
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agents.length}</p>
                <p className="text-xs text-muted-foreground">Agentes configurados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agents.filter((a: any) => a.isActive).length}</p>
                <p className="text-xs text-muted-foreground">Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <MessageSquare className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{operationalOverview?.summary?.salonsWithoutAgent ?? "—"}</p>
                <p className="text-xs text-muted-foreground">Salões sem agente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-2xl font-bold">{operationalOverview?.summary?.needingAttention ?? "—"}</p>
                <p className="text-xs text-muted-foreground">Para revisar</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AiAgentOperationsOverview
        overview={operationalOverview}
        isLoading={loadingOperationalOverview}
        onSelectAgent={(id) => { setSelectedAgentId(id); setSelectedSessionId(null); }}
        onCreateForSalon={(salonId, salonName) => {
          setForm({ ...defaultForm, salonId, name: `Assistente ${salonName}` });
          setShowCreateDialog(true);
        }}
      />

      {/* Layout principal: lista + detalhe */}
      <div className="grid grid-cols-[320px_1fr] gap-6">
        {/* Lista de agentes */}
        <div className="space-y-3" data-testid="agent-list">
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar agente ou salão..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={healthFilter} onValueChange={(value: any) => setHealthFilter(value)}>
              <SelectTrigger data-testid="agent-health-filter"><SelectValue placeholder="Filtrar saúde" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os agentes</SelectItem>
                <SelectItem value="critico">Revisar agora</SelectItem>
                <SelectItem value="atencao">Atenção</SelectItem>
                <SelectItem value="saudavel">Saudáveis</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingAgents ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Carregando...</div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {search ? "Nenhum agente encontrado." : "Nenhum agente configurado ainda."}
            </div>
          ) : (
            filteredAgents.map((agent: any) => {
              const operationalAgent = operationalOverview?.agents?.find((item: any) => item.id === agent.id);
              const healthLabel: Record<string, string> = { saudavel: "Saudável", atencao: "Atenção", critico: "Revisar" };
              return <Card
                key={agent.id}
                data-testid={`agent-card-${agent.id}`}
                className={`cursor-pointer transition-colors hover:border-primary/50 ${
                  selectedAgentId === agent.id ? "border-primary bg-primary/5" : ""
                }`}
                onClick={() => {
                  setSelectedAgentId(agent.id);
                  setSelectedSessionId(null);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`p-1.5 rounded-full ${agent.isActive ? "bg-green-500/10" : "bg-muted"}`}>
                        <Bot className={`h-4 w-4 ${agent.isActive ? "text-green-500" : "text-muted-foreground"}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{agent.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{getSalonName(agent.salonId)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                        {(agent as any).channel === "whatsapp" ? "📱 WA" : (agent as any).channel === "chat_web" ? "💻 Web" : "📱💻 Ambos"}
                      </Badge>
                      <Badge variant={agent.isActive ? "default" : "secondary"} className="text-xs">
                        {agent.isActive ? "Ativo" : "Inativo"}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  {operationalAgent?.alerts?.[0] && <p className="mt-2 text-xs text-muted-foreground truncate">{healthLabel[operationalAgent.health]} · {operationalAgent.alerts[0]}</p>}
                </CardContent>
              </Card>
            })
          )}
        </div>

        {/* Detalhe do agente selecionado */}
        {selectedAgent ? (
          <div className="space-y-4">
            {/* Header do agente */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  {selectedAgent.name}
                </h2>
                <p className="text-sm text-muted-foreground">{getSalonName(selectedAgent.salonId)}</p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link href={`/superadmin/agentes/teste?salonId=${selectedAgent.salonId}`}>
                    <FlaskConical className="h-4 w-4 mr-1" /> Testar agente
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateMutation.mutate({
                      agentId: selectedAgent.id,
                      isActive: !selectedAgent.isActive,
                    });
                  }}
                >
                  {selectedAgent.isActive ? (
                    <><XCircle className="h-4 w-4 mr-1" /> Desativar</>
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-1" /> Ativar</>
                  )}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(selectedAgent)}>
                  <Pencil className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    setDeleteAgentId(selectedAgent.id);
                    setShowDeleteDialog(true);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Remover
                </Button>
              </div>
            </div>

            {selectedOperationalAgent && (
              <Card className="border-primary/20 bg-primary/[0.03]">
                <CardContent className="space-y-3 py-3">
                  <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <div><p className="text-xs text-muted-foreground">Saúde</p><p className="font-semibold capitalize">{selectedOperationalAgent.health}</p></div>
                  <div><p className="text-xs text-muted-foreground">Conversas</p><p className="font-semibold">{selectedOperationalAgent.sessions}</p></div>
                  <div><p className="text-xs text-muted-foreground">Mensagens</p><p className="font-semibold">{selectedOperationalAgent.messages.toLocaleString("pt-BR")}</p></div>
                  <div><p className="text-xs text-muted-foreground">Satisfação</p><p className="font-semibold">{selectedOperationalAgent.satisfactionRate === null ? "Sem avaliação" : `${selectedOperationalAgent.satisfactionRate}%`}</p></div>
                  <div><p className="text-xs text-muted-foreground">Canal</p><p className="font-semibold">{selectedOperationalAgent.channel === "whatsapp" ? "WhatsApp" : selectedOperationalAgent.channel === "chat_web" ? "Chat web" : "WhatsApp + Web"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Última atividade</p><p className="font-semibold">{selectedOperationalAgent.lastActivityAt ? new Date(selectedOperationalAgent.lastActivityAt).toLocaleDateString("pt-BR") : "Sem atividade"}</p></div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <p className="text-sm text-muted-foreground">{selectedOperationalAgent.alerts?.length ? `Próxima ação: ${selectedOperationalAgent.alerts[0]}` : "Configuração e atividade em boa condição."}</p>
                    <div className="flex gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link href={`/superadmin/agentes/teste?salonId=${selectedAgent.salonId}`}>
                          <FlaskConical className="h-4 w-4 mr-1" /> Testar agente
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(selectedAgent)}>Revisar configuração</Button>
                      <Button size="sm" variant="outline" onClick={() => setActiveAgentTab("sessions")}>Ver conversas</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs value={activeAgentTab} onValueChange={setActiveAgentTab}>
              {/* Mobile: caixa suspensa */}
              <div className="block md:hidden mb-2">
                <Select value={activeAgentTab} onValueChange={setActiveAgentTab}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione a seção" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="config">Configurações</SelectItem>
                    <SelectItem value="sessions">Conversas ({sessions.length})</SelectItem>
                    <SelectItem value="history">Histórico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Desktop: abas normais */}
              <TabsList className="hidden md:inline-flex">
                <TabsTrigger value="config">Configurações</TabsTrigger>
                <TabsTrigger value="sessions">Conversas ({sessions.length})</TabsTrigger>
                <TabsTrigger value="history">Histórico</TabsTrigger>
              </TabsList>

              {/* Aba de Configurações */}
              <TabsContent value="config" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Identidade</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nome</span>
                        <span className="font-medium">{selectedAgent.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tom</span>
                        <span className="font-medium capitalize">{selectedAgent.tone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Idioma</span>
                        <span className="font-medium">{selectedAgent.language}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Limite resposta</span>
                        <span className="font-medium">{selectedAgent.maxResponseLength} chars</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Capacidades</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {[
                        { key: "canBook", label: "Criar agendamentos" },
                        { key: "canCancel", label: "Cancelar" },
                        { key: "canReschedule", label: "Reagendar" },
                        { key: "canAnswerFaq", label: "Responder FAQ" },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-muted-foreground">{label}</span>
                          {(selectedAgent as any)[key] ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {selectedAgent.welcomeMessage && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Mensagem de Boas-vindas</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{selectedAgent.welcomeMessage}</p>
                    </CardContent>
                  </Card>
                )}

                {selectedAgent.personality && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Instruções de Personalidade</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedAgent.personality}</p>
                    </CardContent>
                  </Card>
                )}

                {selectedAgent.faqItems && (() => {
                  try {
                    const items = JSON.parse(selectedAgent.faqItems) as FaqItem[];
                    if (items.length === 0) return null;
                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">FAQ ({items.length} itens)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {items.map((faq, idx) => (
                            <div key={idx} className="text-sm border-b last:border-0 pb-2 last:pb-0">
                              <p className="font-medium">{faq.question}</p>
                              <p className="text-muted-foreground">{faq.answer}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  } catch { return null; }
                })()}
              </TabsContent>

              {/* Aba de Conversas */}
              <TabsContent value="sessions">
                <div className="grid grid-cols-[280px_1fr] gap-4">
                  {/* Lista de sessões */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                      Sessões recentes
                    </p>
                    {sessions.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma conversa ainda.</p>
                    ) : (
                      sessions.map((session: any) => (
                        <Card
                          key={session.id}
                          className={`cursor-pointer hover:border-primary/50 transition-colors ${
                            selectedSessionId === session.id ? "border-primary bg-primary/5" : ""
                          }`}
                          onClick={() => setSelectedSessionId(session.id)}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {session.clientName ?? session.clientPhone ?? "Anônimo"}
                                </p>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Badge variant="outline" className="text-xs py-0">
                                    {session.channel}
                                  </Badge>
                                  <span>{session.messageCount} msgs</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  {/* Mensagens da sessão */}
                  <div>
                    {selectedSessionId ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {messages.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">Sem mensagens.</p>
                        ) : (
                          messages.map((msg: any) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                  msg.role === "user"
                                    ? "bg-primary text-primary-foreground"
                                    : msg.role === "assistant"
                                    ? "bg-muted"
                                    : "bg-yellow-500/10 text-yellow-700 text-xs italic"
                                }`}
                              >
                                <p>{msg.content}</p>
                                <p className="text-xs opacity-60 mt-1">
                                  {new Date(msg.createdAt).toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                  {msg.processingMs && ` · ${msg.processingMs}ms`}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                        Selecione uma sessão para ver as mensagens.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Aba de Histórico de Eventos */}
              <TabsContent value="history" className="space-y-4">
                {sessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Activity className="h-10 w-10 mb-3 opacity-20" />
                    <p className="text-sm">Nenhum evento registrado ainda.</p>
                    <p className="text-xs mt-1">Os eventos aparecem quando o agente começar a receber mensagens.</p>
                  </div>
                ) : (() => {
                  // Agrupar sessões por dia
                  const grouped: Record<string, typeof sessions> = {};
                  sessions.forEach((s: any) => {
                    const day = new Date(s.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
                    if (!grouped[day]) grouped[day] = [];
                    grouped[day].push(s);
                  });
                  return (
                    <div className="space-y-6">
                      {/* Resumo de atividade */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg border bg-card text-center">
                          <p className="text-2xl font-bold text-primary">{sessions.length}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Conversas</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card text-center">
                          <p className="text-2xl font-bold text-blue-500">
                            {sessions.reduce((acc: number, s: any) => acc + (s.messageCount ?? 0), 0)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">Mensagens</p>
                        </div>
                        <div className="p-3 rounded-lg border bg-card text-center">
                          <p className="text-2xl font-bold text-green-500">
                            {new Set(sessions.map((s: any) => s.clientPhone ?? s.clientName ?? s.id)).size}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">Clientes únicos</p>
                        </div>
                      </div>

                      {/* Linha do tempo por dia */}
                      <div className="space-y-4">
                        {Object.entries(grouped).map(([day, daySessions]) => (
                          <div key={day}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="h-px flex-1 bg-border" />
                              <span className="text-xs font-medium text-muted-foreground px-2 py-0.5 rounded-full bg-muted">{day}</span>
                              <div className="h-px flex-1 bg-border" />
                            </div>
                            <div className="space-y-2">
                              {daySessions.map((s: any) => (
                                <div key={s.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:border-primary/30 transition-colors">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                                    s.channel === "whatsapp" ? "bg-green-100" :
                                    s.channel === "web" ? "bg-blue-100" : "bg-muted"
                                  }`}>
                                    {s.channel === "whatsapp" ? (
                                      <MessageSquare className="h-4 w-4 text-green-600" />
                                    ) : (
                                      <User className="h-4 w-4 text-blue-600" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm font-medium">
                                        {s.clientName ?? s.clientPhone ?? "Usuário Anônimo"}
                                      </p>
                                      <Badge variant="outline" className="text-xs py-0">{s.channel}</Badge>
                                      <Badge variant={s.status === "active" ? "default" : "secondary"} className="text-xs py-0">
                                        {s.status === "active" ? "Ativa" : s.status === "closed" ? "Encerrada" : "Inativa"}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                      <span className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3" />
                                        {s.messageCount} mensagens
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {new Date(s.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                      </span>
                                      {s.lastMessageAt && (
                                        <span>Última: {new Date(s.lastMessageAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs shrink-0"
                                    onClick={() => {
                                      setSelectedSessionId(s.id);
                                      // Switch para aba de conversas
                                      const tab = document.querySelector('[data-value="sessions"]') as HTMLElement;
                                      tab?.click();
                                    }}
                                  >
                                    Ver <ChevronRight className="h-3 w-3 ml-0.5" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Bot className="h-12 w-12 mb-3 opacity-20" />
            <p className="text-sm">Selecione um agente para ver os detalhes</p>
          </div>
        )}
      </div>

      {/* Dialog: Criar Agente */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Agente de IA</DialogTitle>
            <DialogDescription>
              Configure um assistente virtual nativo para um salão. Apenas o Super Admin pode criar e editar agentes.
            </DialogDescription>
          </DialogHeader>
          <AgentForm
            form={form}
            setForm={setForm}
            newFaq={newFaq}
            setNewFaq={setNewFaq}
            showSalonSelect={true}
            salons={salonsWithoutAgent}
            voiceProvider={voiceProvider}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!form.salonId || !form.name || createMutation.isPending}
            >
              {createMutation.isPending ? "Criando..." : "Criar Agente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Agente */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Agente</DialogTitle>
            <DialogDescription>
              Atualize as configurações do agente. Salão: <strong>{getSalonName(form.salonId)}</strong>
            </DialogDescription>
          </DialogHeader>
          <AgentForm
            form={form}
            setForm={setForm}
            newFaq={newFaq}
            setNewFaq={setNewFaq}
            showSalonSelect={false}
            salons={[]}
            voiceProvider={voiceProvider}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: Deletar */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover agente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O histórico de conversas também será preservado no banco, mas o agente será desativado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAgentId && deleteMutation.mutate({ agentId: deleteAgentId })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
